import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { BUILD_VERSION, APP_VERSION } from '../utils/buildInfo';
import { Sun, Moon } from 'lucide-react';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import LivePresence from '../components/LivePresence';
import GoogleSignInButton from '../components/auth/GoogleSignInButton';
import ShowcaseReel from '../components/onboarding/ShowcaseReel';
import SellingSteps from '../components/onboarding/SellingSteps';
import DesktopOnlyWall from '../components/onboarding/DesktopOnlyWall';
import { stashOnboardingIntent } from '../components/onboarding/onboardingIntent';
import { useDesktopSignInWall } from '../hooks/useDesktopSignInWall';
import { useGoogleOneTap } from '../hooks/useGoogleOneTap';
import { LANDING_PURPOSES, type PurposeDef } from '../components/onboarding/purposes';
import { clearReturnPath, peekReturnPath } from '../utils/returnPath';


const Landing: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  // The purpose the visitor picked, held through the Google redirect so the wait is branded (FR2).
  const [pendingPurpose, setPendingPurpose] = useState<PurposeDef | null>(null);
  // Restore the landing title/canonical when navigating back to "/" within the SPA.
  useDocumentMeta({
    title: 'Wisetree AI — the video studio copilot',
    description: 'Write a micro-drama series, let an AI crew shoot it, and open a storefront to sell it episode by episode.',
    canonical: 'https://wisetree.ai/',
  });
  const { signInWithGoogle, signInWithGoogleCredential, user } = useAuth();
  const { effectiveTheme, setThemeMode } = useTheme();
  // RF3-DEVICE: browsing this page on a phone is fine and wanted; SIGNING IN there is what we intercept.
  const wall = useDesktopSignInWall('landing');

  /**
   * ONE TAP for a returning creator — a prompt Google renders itself, already knowing who they are, instead
   * of a full redirect round trip back into their own studio.
   *
   * Suppressed while a sign-in is already running or the device wall is up, so two sign-ins can never race;
   * the hook suppresses itself on a handheld (the wall intercepts phone sign-in anyway) and whenever no
   * client id is configured. The credential goes through the SAME pipeline as the button — nothing about
   * being one tap relaxes the allowlist or the audit.
   */
  useGoogleOneTap({
    enabled: !user && !isLoading && !wall.open,
    onCredential: (idToken) => {
      setIsLoading(true);
      signInWithGoogleCredential(idToken)
        .catch((err) => setError(err instanceof Error ? err.message : 'Sign-in failed. Please try again.'))
        .finally(() => setIsLoading(false));
    },
  });

  // Clear AFTER the redirect commits (a render-time clear would lose the path on a StrictMode double render).
  useEffect(() => {
    if (user) clearReturnPath();
  }, [user]);

  if (user) {
    return <Navigate to={peekReturnPath() ?? '/dashboard'} replace />;
  }

  const runGoogleSignIn = async () => {
    setError('');
    setIsLoading(true);
    try {
      await signInWithGoogle();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign-in failed. Please try again.');
      setPendingPurpose(null); // sign-in failed — don't keep claiming a studio is being set up
    } finally {
      setIsLoading(false);
    }
  };

  // Purpose-first onboarding: stash the intent, then take the user through Google sign-in. ChatWorkspace
  // reads the intent post-auth and lands them in the ready studio (mirrors the wt_start_agent handoff).
  // Nothing is stashed until we ACTUALLY leave for Google, so a wall that is dismissed leaves no residue.
  const startSignIn = (purpose: PurposeDef | null) => {
    if (purpose) {
      stashOnboardingIntent({ purpose: purpose.key });
      // FR2: reflect the choice back while auth runs — the wait reads as "your studio is being prepared",
      // not "a generic login is loading". Costs nothing and keeps the purpose present through the redirect.
      setPendingPurpose(purpose);
    }
    void runGoogleSignIn();
  };

  // Every route to Google on this page goes through the wall: it passes straight through on a workstation.
  const requestSignIn = (purpose: PurposeDef | null) => wall.requestSignIn(() => startSignIn(purpose));

  /*
   * Staging renders the SAME landing as production (owner decision 2026-08-13). It previously showed a bare
   * sign-in box, which meant the one environment where we can safely rehearse the first-run experience was the
   * one place it could not be seen — every landing change had to be validated in production or not at all.
   *
   * ACCESS IS UNCHANGED: the team allowlist is enforced at SIGN-IN by `enforceStagingAllowlist` in
   * AuthContext, which signs out any account not on the list. Showing the page is not granting entry.
   */

  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-black">
      {/* Theme toggle — light/dark only (first visit follows the OS preference) */}
      <div className="fixed top-6 left-6 z-50">
        {/* Theme toggle hidden 2026-08-29 — dark is the identity (see ThemeContext.FORCE_DARK). */}
        <button
          hidden
          onClick={() => setThemeMode(effectiveTheme === 'dark' ? 'light' : 'dark')}
          className="p-2 rounded-lg text-secondary-400 hover:text-secondary-900 dark:text-secondary-500 dark:hover:text-white hover:bg-secondary-100 dark:hover:bg-secondary-800 transition-colors"
          aria-label={effectiveTheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          title={effectiveTheme === 'dark' ? 'Light mode' : 'Dark mode'}
        >
          {effectiveTheme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
        </button>
      </div>

      {/* A QUIET WAY IN ABOVE THE FOLD. At 1440x900 the one branded button sits below the reel, so a returning
          creator had to scroll to find the door. This is plain text, not a second Google button (owner
          2026-09-12), and it runs the same purpose-carrying sign-in as the button below. */}
      <div className="fixed top-5 right-6 z-50">
        <button
          type="button"
          onClick={() => requestSignIn(LANDING_PURPOSES[0] ?? null)}
          disabled={isLoading}
          className="rounded-md px-3 py-1.5 text-[13px] font-medium text-secondary-600 hover:text-secondary-900 hover:bg-secondary-100 dark:text-secondary-300 dark:hover:text-white dark:hover:bg-secondary-800 transition-colors disabled:opacity-50"
        >
          Sign in
        </button>
      </div>

      {/*
        ===== THE WHOLE OFFER ABOVE THE FOLD (LAND-1, owner 2026-09-26: *"user have to scroll all the way down
        because the video viewer is so long... keep it direct, concise, clear what we offer, and what is in it
        for user, less fluffs"*). Direction 3a of the Claude Design landing review.

        One screen, two columns: WHAT it is and WHAT YOU GET on the left (headline, one sentence, the one
        branded sign-in, the three steps ending in the money step), the reel beside it as proof rather than a
        full-height gate in front of the ask. Everything the 2026-09-12 decisions settled still holds: one
        Google-branded button, the create-then-sell message, and no promise of a cash payday (selling is on the
        Plus plan, creators are paid in credits). The separate steps section and the separate ask at the end are
        folded in here, which is where the scrolling went.
      */}
      <section className="px-4 pt-16 pb-10 sm:px-8 lg:pt-20">
        <div className="mx-auto grid max-w-6xl items-center gap-10 lg:grid-cols-[minmax(0,1fr)_auto] lg:gap-16">
          <div className="flex flex-col gap-6">
            <div className="flex items-center gap-2.5">
              <img
                src="/jiro_ai_logo-light.jpg"
                alt=""
                className="h-8 w-8 rounded-full object-cover ring-1 ring-secondary-200 dark:ring-secondary-700"
              />
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-secondary-500 dark:text-secondary-400">
                Wisetree AI · Micro-drama studio
              </span>
            </div>
            <div className="flex flex-col gap-4">
              <h1 className="text-4xl font-bold tracking-tight text-secondary-900 dark:text-white md:text-5xl lg:text-6xl">
                Turn your story into a series you can sell.
              </h1>
              <p className="max-w-xl text-base leading-relaxed text-secondary-600 dark:text-secondary-400 md:text-lg">
                You bring the idea. An AI writer, director and cast make the episodes, with the same faces from the
                first episode to the last.{' '}
                <span className="text-secondary-900 dark:text-white">Then you sell them from your own store.</span>
              </p>
            </div>

            <div className="flex flex-col gap-2">
              <div className="flex flex-wrap items-center gap-4">
                <GoogleSignInButton onClick={() => requestSignIn(LANDING_PURPOSES[0] ?? null)} disabled={isLoading} />
                <span className="text-sm text-secondary-500 dark:text-secondary-400">
                  Your first storyboard is on us.
                </span>
              </div>
              {isLoading && (
                <div className="flex items-center gap-2 text-sm text-secondary-500 dark:text-secondary-400">
                  <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-secondary-500" />
                  <span>
                    {pendingPurpose
                      ? `Setting up your ${pendingPurpose.label.toLowerCase()} studio…`
                      : 'Opening your studio…'}
                  </span>
                </div>
              )}
              {error && <p className="text-sm text-secondary-700 dark:text-secondary-300" role="alert">{error}</p>}
            </div>

            <SellingSteps compact />
          </div>

          {/* PROOF BESIDE THE OFFER, not in front of it: the reel is the output, sized to sit within the fold. */}
          <div className="flex flex-col items-center gap-3 lg:w-[300px]">
            <ShowcaseReel className="w-full max-w-[300px]" />
            <div className="hidden sm:flex"><LivePresence /></div>
          </div>
        </div>
      </section>

      {/*
        ONE SIGN-IN BUTTON ON THE PAGE (owner 2026-09-12: *"I don't like how we have 2 Google branded login
        buttons within same viewport, not much point having two buttons"*).

        A closing CTA was added on 2026-08-22 for a real reason — the page was long, and the only way in was
        back at the top. That reason has expired: the page is now short enough that the hero's card is in view
        on a workstation, so the second control was not an ask at the end of an argument, it was the same ask
        twice. Two Google-branded buttons in one viewport also read as unconsidered, which is a poor first
        impression for a product selling craft.

        It was also the WEAKER of the two. The hero's card signs in WITH the purpose the visitor picked; this
        one passed `null`, dropping the purpose-first onboarding the picker exists to carry — so a visitor who
        used it landed in a generic studio rather than a prepared one.

        IF THE PAGE GROWS past roughly one and a half viewports, the ask at the end earns its place again — but
        as a quiet control that returns the visitor to the purpose card, never as a second branded button.
      */}

      {/* The two positioning cards ("Continuity that holds to the last frame" / "Yours to keep") were
           removed on 2026-08-22 — the page states its case with the reels and the pipeline; a claims grid
           underneath was telling rather than showing. */}
      <section className="px-4 pb-14">
        <p className="mx-auto max-w-md text-center text-xs text-secondary-400 dark:text-secondary-500">
          By continuing, you agree to our{' '}
          <a href="/terms" className="underline hover:text-secondary-600 dark:hover:text-white">Terms</a>
          {' '}&amp;{' '}
          <a href="/privacy" className="underline hover:text-secondary-600 dark:hover:text-white">Privacy</a>
        </p>
      </section>

      {/* The wall itself — raised only by a sign-in attempt on a handheld, never by arriving here. */}
      <DesktopOnlyWall
        open={wall.open}
        onContinueAnyway={wall.continueAnyway}
        onDismiss={wall.dismiss}
      />

      {/* ===== FOOTER ===== */}
      <footer className="border-t border-secondary-200 dark:border-secondary-800 py-6">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="text-xs text-secondary-400">
            &copy; 2026 Wisetree AI Pty Ltd. All rights reserved.
          </p>
          <div className="flex items-center gap-5 text-xs">
            <a href="/pricing" className="text-secondary-400 hover:text-secondary-600 dark:hover:text-white transition-colors">Pricing</a>
            <a href="/tutorials" className="text-secondary-400 hover:text-secondary-600 dark:hover:text-white transition-colors">Tutorials</a>
            <a href="/faq" className="text-secondary-400 hover:text-secondary-600 dark:hover:text-white transition-colors">FAQ</a>
            <a href="/privacy" className="text-secondary-400 hover:text-secondary-600 dark:hover:text-white transition-colors">Privacy</a>
            <a href="/terms" className="text-secondary-400 hover:text-secondary-600 dark:hover:text-white transition-colors">Terms</a>
          </div>
        </div>
        {/* Build/version stamp — auto-iterated on each staging/main deploy so you can tell which
            environment is running an older vs newer build. Coloured to MATCH the page background
            (text-white / dark:text-black) so it's effectively invisible to mainstream users but
            still in the DOM + selectable for support. APP_VERSION already carries a leading "v",
            so strip it to render one "v", not "vv". */}
        <p className="mt-3 text-center text-[11px] text-white dark:text-black font-mono select-text">
          v{String(APP_VERSION).replace(/^v+/, '')} · build {BUILD_VERSION}
        </p>
      </footer>
    </div>
  );
};

export default Landing;
